import type { RepeatingSchedule } from '@/types/schedule';

import { convertUTCToLocalTime, getBrowserTimezone } from '../utils/timezoneData';

type ScheduleTimeDisplayMode = 'local' | 'utc';

function formatLocalTime12h(hour: number, minute: number): string {
  const h12 = hour % 12 || 12;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const minStr = minute.toString().padStart(2, '0');
  return `${h12}:${minStr} ${ampm}`;
}

function formatUtcTimeLabel(timeUTC: string): string {
  const [hStr, mStr] = timeUTC.split(':');
  return formatLocalTime12h(parseInt(hStr, 10), parseInt(mStr, 10));
}

function formatStoredUtcInViewerLocal(
  timeUTC: string,
  viewerTimeZone: string = getBrowserTimezone()
): string {
  const [hStr, mStr] = timeUTC.split(':');
  const local = convertUTCToLocalTime(parseInt(hStr, 10), parseInt(mStr, 10), viewerTimeZone);
  return formatLocalTime12h(local.hour, local.minute);
}

/**
 * Format repeating interval with optional starting-at time.
 * Local mode: viewer's browser timezone (aligned with Next Run).
 * UTC mode: stored UTC time.
 */
export function formatRepeatingScheduleFrequency(
  repeating: RepeatingSchedule,
  mode: ScheduleTimeDisplayMode,
  viewerTimeZone: string = getBrowserTimezone()
): string {
  const { frequency: freq, frequencyUnit, startingAt } = repeating;
  const unit = frequencyUnit === 'hour' ? 'hr' : 'min';
  const plural = freq !== 1 ? 's' : '';
  const base = freq === 0 ? `0 ${unit}s` : `🔁 ${freq} ${unit}${plural}`;

  if (!startingAt) {
    return base;
  }

  if (mode === 'utc') {
    return `${base}, starting at ${formatUtcTimeLabel(startingAt)} UTC`;
  }

  return `${base}, starting at ${formatStoredUtcInViewerLocal(startingAt, viewerTimeZone)}`;
}
